import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  ArrowRight,
  CalendarDays,
  CheckCircle2,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  Trophy,
} from "lucide-react";
import { toast } from "sonner";

import { API, useAuth } from "../App";
import AdminLayout from "../components/AdminLayout";
import EditorialPageHeader from "../components/EditorialPageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import TournamentOperationsWorkspace from "../components/tournament/TournamentOperationsWorkspace";
import TournamentSetupWizard from "../components/tournament/TournamentSetupWizard";

const today = new Date().toISOString().slice(0, 10);
const DRAFT_STORAGE_KEY = "sporthouse:tournament-setup-draft";
const requestKey = () =>
  `draft_${globalThis.crypto?.randomUUID?.().replaceAll("-", "") || `${Date.now()}_${Math.random().toString(36).slice(2)}`}`;

const initialForm = () => ({
  idempotency_key: requestKey(),
  name: "",
  sport: "soccer",
  participant_type: "team",
  expected_participants: 16,
  pool_size: 4,
  starts_on: today,
  ends_on: today,
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Los_Angeles",
  payment_mode: "free",
  location: "",
  registration_closes_on: today,
  registration_categories: [{ idempotency_key: requestKey().replace("draft_", "division_"), name: "Open", age_group: "Open", gender: "open" }],
  capacity: 16,
  entry_fee_dollars: 0,
  payment_timing: "after_approval",
});

const EmbeddedLayout = ({ children, title, subtitle, actions, headerEyebrow }) => (
  <>
    <EditorialPageHeader
      eyebrow={headerEyebrow}
      title={title}
      description={subtitle}
      aside={actions}
      headingId="admin-page-title"
      className="mb-6"
    />
    {children}
  </>
);

const AdminTournamentModule = ({ embedded = false }) => {
  const PageLayout = embedded ? EmbeddedLayout : AdminLayout;
  const { token } = useAuth();
  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token],
  );
  const [metadata, setMetadata] = useState(null);
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [workspace, setWorkspace] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [openingId, setOpeningId] = useState(null);
  const [query, setQuery] = useState("");
  const [form, setForm] = useState(() => {
    try {
      const saved = window.localStorage.getItem(DRAFT_STORAGE_KEY);
      return saved ? { ...initialForm(), ...JSON.parse(saved) } : initialForm();
    } catch {
      return initialForm();
    }
  });

  const loadWorkspace = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const [metaResponse, listResponse] = await Promise.all([
        axios.get(`${API}/modules/tournaments/metadata`, { headers }),
        axios.get(`${API}/modules/tournaments`, { headers }),
      ]);
      setMetadata(metaResponse.data);
      setTournaments(listResponse.data.tournaments || []);
    } catch (error) {
      const message = error.response?.data?.detail || "Tournament module could not be loaded";
      setLoadError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    loadWorkspace();
  }, [loadWorkspace]);

  useEffect(() => {
    if (!creating) return;
    window.localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(form));
  }, [creating, form]);

  const update = (key, value) =>
    setForm((current) => ({ ...current, [key]: value }));

  const openTournament = async (tournament) => {
    setOpeningId(tournament.tournament_id);
    try {
      const response = await axios.get(
        `${API}/modules/tournaments/${tournament.tournament_id}/workspace`,
        { headers },
      );
      setWorkspace(response.data);
      setCreating(false);
    } catch (error) {
      toast.error(
        error.response?.data?.detail ||
          "Tournament workspace could not be loaded",
      );
    } finally {
      setOpeningId(null);
    }
  };

  const visibleTournaments = tournaments.filter((item) => {
    const searchText = `${item.name || ""} ${item.sport || ""} ${item.status || ""}`.toLowerCase();
    return searchText.includes(query.trim().toLowerCase());
  });

  const saveDraft = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const response = await axios.post(
        `${API}/modules/tournaments`,
        {
          idempotency_key: form.idempotency_key,
          name: form.name,
          sport: form.sport,
          participant_type: form.participant_type,
          expected_participants: Number(form.capacity),
          pool_size: Number(form.pool_size),
          starts_on: form.starts_on,
          ends_on: form.ends_on,
          timezone: form.timezone,
          payment_mode: Number(form.entry_fee_dollars) > 0 ? "delegated" : "free",
          payment_timing: form.payment_timing,
          location: form.location,
          registration_closes_on: form.registration_closes_on,
        },
        { headers },
      );
      await Promise.all(form.registration_categories.map((category) => axios.post(
        `${API}/modules/tournaments/${response.data.tournament.tournament_id}/divisions`,
        {
          idempotency_key: category.idempotency_key,
          name: category.name,
          classification: category.age_group,
          age_group: category.age_group,
          competitive_level: "open",
          gender: category.gender,
          capacity: Number(form.capacity),
          entry_fee_cents: Math.round(Number(form.entry_fee_dollars) * 100),
          format_template: null,
          pool_size: 4,
          required_documents: [],
          eligibility_notes: "",
        },
        { headers },
      )));
      setTournaments((current) => [response.data.tournament, ...current]);
      setCreating(false);
      window.localStorage.removeItem(DRAFT_STORAGE_KEY);
      setForm(initialForm());
      toast.success("Tournament draft created");
      // Returning to an open workspace must not replace its unsaved local edits.
      if (!workspace) await openTournament(response.data.tournament);
    } catch (error) {
      toast.error(
        error.response?.data?.detail?.[0]?.msg ||
          error.response?.data?.detail ||
          "Tournament draft could not be created",
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return (
      <PageLayout title="Tournaments" subtitle="Build, publish, and operate every competition from one workspace." headerEyebrow="Competition">
        <div className="min-h-[70vh] bg-[#fbfcf8]">
          <div role="status" className="flex items-center rounded-[24px] border border-[#dfe6df] bg-white p-6 text-sm text-[#102a27] sm:p-8">
            <Loader2 aria-hidden="true" className="mr-2 h-5 w-5 animate-spin text-[#087f67]" />
            Loading tournament workspace
          </div>
        </div>
      </PageLayout>
    );

  return (
    <PageLayout
      title="Tournaments"
      subtitle="Build, publish, and operate every competition from one workspace."
      headerEyebrow="Competition"
      actions={<Button onClick={() => setCreating(true)} className="w-full rounded-full bg-[#b7f34a] text-[#102a27] hover:bg-[#c7f66f] sm:w-auto"><Plus className="mr-2 h-4 w-4" />New tournament</Button>}
    >
      <div className="space-y-6" data-testid="tournament-module-workspace">
        {/* Keep the same subtree mounted so Cancel restores child-owned drafts. */}
        {workspace && (
          <div hidden={creating || Boolean(loadError)}>
            <TournamentOperationsWorkspace
              workspace={workspace}
              metadata={metadata}
              headers={headers}
              onBack={() => setWorkspace(null)}
              onRefresh={() => openTournament(workspace.tournament)}
            />
          </div>
        )}
        {loadError ? (
          <section className="rounded-[24px] border border-red-200 bg-white p-6 sm:p-8" role="alert">
            <div className="max-w-xl">
              <p className="font-semibold text-[#102a27]">Tournaments could not be loaded</p>
              <p className="mt-2 text-sm text-[#60706a]">{loadError}</p>
              <Button variant="outline" className="mt-5 rounded-xl" onClick={loadWorkspace}>
                <RefreshCw className="mr-2 h-4 w-4" /> Try again
              </Button>
            </div>
          </section>
        ) : creating ? (
          <div className="space-y-4">
            {workspace && <p className="rounded-xl border border-[#dfe6df] bg-[#eef8f3] p-4 text-sm text-[#31534c]">Your open tournament and unsaved edits are preserved. Cancel, Back, or finishing this setup returns you there. Find the new draft in All tournaments.</p>}
            <TournamentSetupWizard
              form={form}
              metadata={metadata}
              saving={saving}
              update={update}
              onCancel={() => setCreating(false)}
              onSubmit={saveDraft}
            />
          </div>
        ) : !workspace ? (
          <section className="rounded-[24px] border border-[#dfe6df] bg-white p-5 sm:p-7">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.18em] text-[#087f67]">
                  Your competitions
                </div>
                <h2 className="mt-2 text-2xl font-semibold text-[#102a27]">
                  Tournament workspace
                </h2>
              </div>
              <div className="text-sm text-[#60706a]">
                Module {metadata?.module_version}
              </div>
            </div>
            {tournaments.length > 0 && (
              <label className="relative mt-6 block max-w-md">
                <span className="sr-only">Search tournaments</span>
                <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#60706a]" />
                <Input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search tournaments"
                  className="h-11 rounded-xl border-[#d8e0d8] pl-10"
                />
              </label>
            )}
            {tournaments.length === 0 ? (
              <div className="mt-6 rounded-[20px] border border-dashed border-[#cbd6cf] bg-[#fbfcf8] p-10 text-center">
                <Trophy className="mx-auto h-9 w-9 text-[#087f67]" />
                <h3 className="mt-4 text-lg font-semibold text-[#102a27]">
                  No tournament drafts yet
                </h3>
                <p className="mx-auto mt-2 max-w-lg text-sm leading-6 text-[#60706a]">
                  Start with a validated competition template. Nothing becomes
                  public until a future publish step is explicitly completed.
                </p>
                <Button
                  onClick={() => setCreating(true)}
                  className="mt-5 rounded-xl bg-[#102a27] text-white"
                >
                  <Plus className="mr-2 h-4 w-4" /> Create tournament
                </Button>
              </div>
            ) : visibleTournaments.length === 0 ? (
              <div className="mt-6 rounded-[20px] border border-dashed border-[#cbd6cf] bg-[#fbfcf8] p-8 text-center">
                <h3 className="font-semibold text-[#102a27]">No tournaments match “{query}”</h3>
                <Button variant="outline" className="mt-4 rounded-xl" onClick={() => setQuery("")}>Clear search</Button>
              </div>
            ) : (
              <div className="mt-6 grid gap-4 lg:grid-cols-2">
                {visibleTournaments.map((item) => (
                  <article
                    key={item.tournament_id}
                    className="rounded-[20px] border border-[#dfe6df] p-5"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-xs font-bold uppercase tracking-wide text-[#087f67]">
                          {metadata?.sports?.[item.sport]?.label}
                        </div>
                        <h3 className="mt-1 text-lg font-semibold text-[#102a27]">
                          {item.name}
                        </h3>
                      </div>
                      <span className="rounded-full bg-[#fff4ce] px-3 py-1 text-xs font-semibold text-[#725a1d]">
                        {(item.status || "draft").replaceAll("_", " ")}
                      </span>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-4 text-sm text-[#60706a]">
                      <span className="flex items-center gap-1">
                        <CalendarDays className="h-4 w-4" /> {item.starts_on} –{" "}
                        {item.ends_on}
                      </span>
                      <span>{item.expected_participants} expected entries</span>
                    </div>
                    <div className="mt-4 flex items-center justify-between border-t border-[#edf0ec] pt-4">
                      <span className="flex items-center gap-1 text-xs font-semibold text-[#087f67]">
                        <CheckCircle2 className="h-4 w-4" /> Structure validated
                      </span>
                      <button
                        onClick={() => openTournament(item)}
                        disabled={openingId === item.tournament_id}
                        className="flex items-center gap-1 text-sm font-semibold text-[#102a27] disabled:opacity-60"
                      >
                        {openingId === item.tournament_id ? <><Loader2 className="h-4 w-4 animate-spin" /> Opening</> : <>Open & edit <ArrowRight className="h-4 w-4" /></>}
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
        ) : null}
      </div>
    </PageLayout>
  );
};

export default AdminTournamentModule;
